import { useEffect, useState, useRef } from "react";

/**
 * Lightweight, client-side A/B test hook.
 *
 *   const { variant, track } = useAbTest("hero_cta_v1", ["control", "wishlist"]);
 *
 *   <Button onClick={() => track("ab_test_click_signup")}>
 *     {variant === "wishlist" ? "Wishlist your next big purchase" : "Start protecting your purchases"}
 *   </Button>
 *
 * Behaviour:
 *   - Assignment is random 50/50 on the visitor's first visit, then persisted in
 *     localStorage under `pg_ab_<testKey>` so the same visitor always sees the
 *     same variant on every subsequent page view.
 *   - Reads localStorage synchronously inside the lazy useState initialiser to
 *     avoid a flash-of-control on first paint.
 *   - Fires a Google Analytics 4 `ab_test_view` event once per page load with
 *     `ab_test_key` and `ab_test_variant` parameters so the experiment can be
 *     segmented in GA4 (requires those two parameters to be registered as
 *     custom dimensions in GA4 admin).
 *   - `track(eventName, extraParams)` wraps `gtag('event', …)` and automatically
 *     attaches the test key + variant — use it on goal events like CTA clicks.
 *
 * SSR / private-mode safety: every localStorage and gtag access is wrapped in
 * try/catch, with a deterministic fallback to the first variant.
 */
export default function useAbTest(testKey, variants = ["control", "treatment"]) {
  const [variant] = useState(() => {
    try {
      if (typeof window === "undefined") return variants[0];
      const storageKey = `pg_ab_${testKey}`;
      const existing = window.localStorage.getItem(storageKey);
      if (existing && variants.includes(existing)) return existing;
      const fresh = variants[Math.floor(Math.random() * variants.length)];
      window.localStorage.setItem(storageKey, fresh);
      return fresh;
    } catch {
      return variants[0];
    }
  });

  const viewFiredRef = useRef(false);

  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "ab_test_view", {
          ab_test_key: testKey,
          ab_test_variant: variant,
        });
      }
    } catch {
      /* gtag not loaded — silent skip */
    }
  }, [testKey, variant]);

  const track = (eventName, extraParams = {}) => {
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", eventName, {
          ab_test_key: testKey,
          ab_test_variant: variant,
          ...extraParams,
        });
      }
    } catch {
      /* gtag not loaded — silent skip */
    }
  };

  return { variant, track };
}
